/**
 * Builds the EasyKey clipboard: the quote the Branch EasyKey Chrome extension
 * (packages/easykey-extension) pastes into EZLynx, so an agent can quote the rest of the panel
 * without retyping the Branch quote. It replaced the EasyKey XML export.
 *
 * Values are in EZLynx's wording (see easykey-values.js and the maps below). Fields Branch doesn't
 * collect (industry, occupation, heating type, distance to the fire station) are left out rather
 * than filled with a default: an empty EZLynx field the agent has to fill is better than a pasted
 * guess they won't notice. The XML export had to invent such values because EZLynx's import
 * rejected a file without them.
 *
 * The extension checks this shape against CLIPBOARD_FIELDS in
 * packages/easykey-extension/src/shared/clipboard.js and drops fields it doesn't know, so a new field
 * has to be added there too.
 *
 * GitHub issue: #29109
 */

import {
  BRANCH_TO_EZLYNX_CONSTRUCTION,
  BRANCH_TO_EZLYNX_DWELLING,
  BRANCH_TO_EZLYNX_DWELLING_USE,
  BRANCH_TO_EZLYNX_EXTERIOR_WALLS,
  BRANCH_TO_EZLYNX_ROOF_DESIGN,
  derivePriorAutoPolicy,
  deriveDriverRelation,
  deriveHomePolicyType,
  elapsedYearsMonths,
  findCoApplicantDriver,
  formatDate,
  formatHomeDeductible,
  isBundle,
  normalizeDeductible,
  normalizeGender,
  normalizeMaritalStatus,
  normalizeMpValue,
  normalizePriorCarrier,
  normalizeRentalDeductible,
  normalizeUMUIMValue,
  splitAddress,
  splitBIPD,
  splitZip,
  translateRoofToEzlynx,
  translateToEzlynx
} from './easykey-values';

export const EASYKEY_CLIPBOARD_VERSION = 1;

// The maps below translate Branch lookup ids into the wording of EZLynx's dropdowns, checked against
// EZLynx staging on 2026-11-04. A Branch value with no clear EZLynx equivalent is left out, so the agent
// answers it rather than finding a guess.

// Branch highestEducation. "High school or less" is mostly diploma holders; "Doctoral" is a PhD
// (EZLynx lists medical and law degrees separately).
const BRANCH_TO_EZLYNX_EDUCATION = {
  H: 'High School Diploma',
  S: 'Some College - No Degree',
  A: 'Associates Degree',
  B: 'Bachelors',
  M: 'Masters',
  D: 'Phd'
};

// Branch car primaryUse.
const BRANCH_TO_EZLYNX_VEHICLE_USE = { P: 'Pleasure', B: 'Business', C: 'To/From Work' };

// Branch vehicleOwnershipType. EZLynx calls a financed car "Lien".
const BRANCH_TO_EZLYNX_VEHICLE_OWNERSHIP = { owned: 'Owned', financed: 'Lien', leased: 'Leased' };

// Branch basementType. "Raised/Crawlspace" is left out: EZLynx splits crawl spaces into enclosed and open.
const BRANCH_TO_EZLYNX_FOUNDATION = {
  S: 'Slab',
  U: 'Basement - Unfinished',
  PF: 'Basement - Partially Finished',
  FF: 'Basement - Finished'
};

// Branch residenceInsuranceType → the auto Coverage screen's "Residence is". Renters ("R") is left out:
// EZLynx asks whether it's an apartment or a rented home, which Branch doesn't record.
const BRANCH_TO_EZLYNX_RESIDENCE = { O: 'Home (owned)', C: 'Condo (owned)', M: 'Mobile Home' };

// When the auto quote has no residence type, a home or condo quote on the insured's primary residence
// answers it. Branch home ownershipStatus P is "Primary", PS "Primary, Owns Secondary".
const RESIDENCE_FROM_POLICY_FORM = { HO3: 'Home (owned)', HO5: 'Home (owned)', HO6: 'Condo (owned)' };
const PRIMARY_RESIDENCE_STATUSES = ['P', 'PS'];

// HO3/HO5/HO6 only insure an owner-occupied home.
const OWNER_OCCUPIED_FORMS = ['HO3', 'HO5', 'HO6'];

// Branch auto policies are written for six months (policyTermMonths in @ourbranch/be-constants, which
// staff-fe doesn't depend on), so EZLynx rates the same term.
const BRANCH_AUTO_POLICY_TERM = '6 Month';

// EZLynx's home Coverage screen asks only which financial interests exist, with a box each for the
// first, second and third mortgagee; the lender's name and loan number aren't part of rating.
const MORTGAGEE_BOXES = ['firstMortgagee', 'secondMortgagee', 'thirdMortgagee'];

// The highest values EZLynx's dropdowns offer: "Years At Address" stops at 15, half baths at "5+".
const MAX_YEARS_AT_ADDRESS = 15;
const MAX_HALF_BATHS = 5;

// Branch comprehensive deductibles with a glass buyback end in "/<glass deductible>GL"; "/0GL" is a $0
// glass deductible, which is what EZLynx's Full Glass checkbox means.
const FULL_GLASS_DEDUCTIBLE = /\/0GL$/i;

/** Which EZLynx quotes the selected option needs. */
export function getEasyKeyLines(selectedOption) {
  const option = selectedOption || '';
  return {
    hasAuto: option.includes('A'),
    hasProperty:
      option.includes('H') || option.includes('R') || option.includes('C') || option === 'Z' || option === 'Y'
  };
}

function yesNo(value) {
  if (value == null) {
    return undefined;
  }
  return value ? 'Yes' : 'No';
}

function wholeDollars(value) {
  const amount = Number(value);
  return value == null || value === '' || !Number.isFinite(amount) ? undefined : Math.round(amount);
}

// EZLynx offers Coverages B, C and D as a percentage of the dwelling ("10%", "50%"), which is also how
// Branch stores them.
function percentOfDwelling(percent) {
  return percent ? `${percent}%` : undefined;
}

/** Drops empty values and stringifies the rest; null when nothing is left. */
function compact(record) {
  const entries = Object.entries(record).filter(([, value]) => value != null && value !== '');
  return entries.length > 0 ? Object.fromEntries(entries.map(([key, value]) => [key, String(value)])) : null;
}

function buildPerson(person) {
  const middleName = person?.middleName?.trim();
  return {
    firstName: person?.firstName,
    lastName: person?.lastName,
    // EZLynx asks for a middle initial only.
    middleInitial: middleName ? middleName.charAt(0).toUpperCase() : undefined,
    // EZLynx lists "JR", "SR", "II"; Branch may hold "Jr.".
    suffix: person?.suffix ? person.suffix.replace(/\./g, '').trim().toUpperCase() : undefined,
    education: BRANCH_TO_EZLYNX_EDUCATION[person?.highestEducation],
    dateOfBirth: formatDate(person?.dateOfBirth || person?.birthDate),
    gender: normalizeGender(person?.gender),
    maritalStatus: normalizeMaritalStatus(person?.maritalStatus)
  };
}

// EZLynx's applicant and co-applicant screens ask for the person's own license; both are drivers.
function buildLicense(driver) {
  return { licenseNumber: driver?.driversLicenseNumber, licenseState: driver?.driversLicenseState };
}

/**
 * Years and months at the current address, from the purchase date of the home being quoted when it's
 * the insured's primary residence (the applicant has lived there since buying it). Branch doesn't
 * otherwise record how long the applicant has lived at the address.
 */
function timeAtAddress({ home, policyForm }) {
  if (!home?.purchaseDate || !PRIMARY_RESIDENCE_STATUSES.includes(home.ownershipStatus)) {
    return {};
  }
  if (!RESIDENCE_FROM_POLICY_FORM[policyForm]) {
    return {};
  }
  const { years, months } = elapsedYearsMonths(home.purchaseDate);
  // Past EZLynx's 15-year maximum the months no longer mean anything.
  return years > MAX_YEARS_AT_ADDRESS
    ? { yearsAtAddress: MAX_YEARS_AT_ADDRESS }
    : { yearsAtAddress: years, monthsAtAddress: months };
}

function buildApplicant({ quote, primaryDriver, residence }) {
  const address = quote.correctedAddress || {};
  const { streetNumber, streetName, unitNumber } = splitAddress(address.address, address.address2);
  return compact({
    ...buildPerson({ ...primaryDriver, firstName: quote.fname, lastName: quote.lname }),
    email: quote.email,
    phone: quote.phone ? String(quote.phone).replace(/\D/g, '') : undefined,
    // The applicant is the primary driver.
    ...buildLicense(primaryDriver),
    ...timeAtAddress(residence),
    address: address.address,
    unit: unitNumber,
    streetNumber,
    streetName,
    city: address.city,
    state: address.state,
    zip: splitZip(address.zip).zip5,
    county: quote.home?.county || address.county
  });
}

function buildDriver(driver) {
  return compact({
    ...buildPerson(driver),
    relation: deriveDriverRelation(driver, Boolean(driver.isPrimary)),
    licenseNumber: driver.driversLicenseNumber,
    licenseState: driver.driversLicenseState,
    ageLicensed: driver.ageFirstLicensed,
    sr22: yesNo(driver.needSR22),
    licenseSuspended: yesNo(driver.lapsedLicense),
    goodStudent: yesNo(driver.goodStudent),
    rated: driver.excludeDriver ? 'Excluded' : 'Rated'
  });
}

function fullGlass(car) {
  if (car.windowGlass != null) {
    return yesNo(car.windowGlass);
  }
  // Staff's offer query doesn't fetch windowGlass; the glass buyback is in the comprehensive deductible.
  return FULL_GLASS_DEDUCTIBLE.test(car.deductibleComprehensive ?? '') ? 'Yes' : undefined;
}

function buildVehicle(car, drivers) {
  const principalDriver = drivers.find((driver) => driver.assignedCar && driver.assignedCar === car.VIN);
  return compact({
    vin: car.VIN,
    year: car.year,
    make: car.make,
    model: car.model,
    bodyType: car.bodyType,
    annualMiles: car.annualMilesDriven ?? car.verifiedAnnualMilesDriven,
    purchaseDate: formatDate(car.purchaseDate),
    vehicleUse: BRANCH_TO_EZLYNX_VEHICLE_USE[car.primaryUse],
    ownershipType: BRANCH_TO_EZLYNX_VEHICLE_OWNERSHIP[car.ownership],
    rideshare: yesNo(car.rideSharing),
    principalOperator: principalDriver
      ? [principalDriver.firstName, principalDriver.lastName].filter(Boolean).join(' ')
      : undefined,
    comprehensive: normalizeDeductible(car.deductibleComprehensive),
    collision: normalizeDeductible(car.deductibleCollision),
    rental: normalizeRentalDeductible(car.limitRental),
    fullGlass: fullGlass(car),
    loanLease: yesNo(car.coverageLoan)
  });
}

// EZLynx's Prior Liability Limits dropdown pairs the prior per-person and per-accident bodily injury
// limits the way its Bodily Injury dropdown does ("100/300"). Branch stores each one in dollars.
function formatPriorLiabilityLimits({ priorIndividualBILimit, priorOccurrenceBILimit }) {
  if (!priorIndividualBILimit || !priorOccurrenceBILimit) {
    return undefined;
  }
  return `${priorIndividualBILimit / 1000}/${priorOccurrenceBILimit / 1000}`;
}

/**
 * EZLynx asks for uninsured motorist property damage once per policy (Ohio: "Uninsured Motorist
 * Property Damage"); Branch sets limitUMPD per car ("NONE" or a dollar amount). Sent only when every
 * car has the same limit.
 */
function uninsuredMotoristPropertyDamage(cars) {
  const limits = [...new Set(cars.map((car) => car.limitUMPD).filter((limit) => limit != null && limit !== ''))];
  if (limits.length !== 1) {
    return undefined;
  }
  return limits[0] === 'NONE' ? 'No Coverage' : limits[0];
}

/**
 * Answers the auto and home quotes both ask for: "Quote as Package" and the Multipolicy Discount box
 * for a bundle, and "Credit Check and Other Underwriting Reports Authorized", which is always Yes
 * (decided 2026-11-04, as the XML export did).
 */
function sharedPolicyAnswers(optionType) {
  const bundled = isBundle(optionType);
  return { quoteAsPackage: yesNo(bundled), multipolicyDiscount: yesNo(bundled), creditCheckAuthorized: 'Yes' };
}

function buildAutoPolicy({ quote, cars, optionType, residenceFromHome }) {
  const coverage = quote.autoCoverage || {};
  const global = quote.global || {};
  const { bi, pd } = splitBIPD(coverage.policyLimitBIPD);
  const prior = derivePriorAutoPolicy(global);
  // derivePriorAutoPolicy reports 0 years when it has no start date; only paste a real duration.
  const hasCarrierStart = Boolean(global.currentlyAutoInsured && global.currentAutoCarrierStartDate);
  const hasContinuousStart = Boolean(
    global.currentlyAutoInsured && (global.continuousAutoCoverageStartDate || global.currentAutoCarrierStartDate)
  );
  return compact({
    effectiveDate: formatDate(global.autoEffectiveDate),
    policyTerm: BRANCH_AUTO_POLICY_TERM,
    ...sharedPolicyAnswers(optionType),
    bodilyInjury: bi,
    propertyDamage: pd,
    medicalPayments:
      coverage.policyLimitMedicalPayments != null ? normalizeMpValue(coverage.policyLimitMedicalPayments) : undefined,
    pip: coverage.policyLimitPIP,
    uninsuredMotorist: normalizeUMUIMValue(coverage.policyLimitUMBI),
    underinsuredMotorist: normalizeUMUIMValue(coverage.policyLimitUIMBI),
    uninsuredMotoristPropertyDamage: uninsuredMotoristPropertyDamage(cars),
    // Branch knows when the customer has no auto insurance today; EZLynx has an answer for that.
    priorCarrier: global.currentlyAutoInsured === false ? 'No Prior Insurance' : prior.priorCarrier,
    priorExpiration: prior.expiration,
    priorLiabilityLimits: formatPriorLiabilityLimits(global),
    yearsWithPriorCarrier: hasCarrierStart ? prior.withCarrier.years : undefined,
    monthsWithPriorCarrier: hasCarrierStart ? prior.withCarrier.months : undefined,
    yearsContinuousCoverage: hasContinuousStart ? prior.continuous.years : undefined,
    monthsContinuousCoverage: hasContinuousStart ? prior.continuous.months : undefined,
    residenceOwnership: BRANCH_TO_EZLYNX_RESIDENCE[quote.auto?.residenceInsuranceType] ?? residenceFromHome
  });
}

// A roof newer than the house was replaced, and one as old
// as the house never was.
function roofUpdate({ roofYear, yearBuilt }) {
  if (!roofYear || !yearBuilt) {
    return undefined;
  }
  if (roofYear > yearBuilt) {
    return 'Complete Update';
  }
  return roofYear === yearBuilt ? 'Not Updated' : undefined;
}

function halfBaths(count) {
  if (!count) {
    return undefined;
  }
  return count >= MAX_HALF_BATHS ? `${MAX_HALF_BATHS}+` : count;
}

/** Ticks one mortgagee box per mortgagee Branch has (the first is the primary lender). */
function mortgageeBoxes(mortgageDetails) {
  const count = Array.isArray(mortgageDetails) ? mortgageDetails.length : 0;
  return Object.fromEntries(MORTGAGEE_BOXES.map((box, index) => [box, index < count ? 'Yes' : undefined]));
}

function buildProperty({ quote, policyForm }) {
  const home = quote.home;
  if (!home) {
    return null;
  }
  const state = home.homeLocation?.state || quote.correctedAddress?.state;
  // swimmingPool is a pool-type code (A/B/N), not a boolean; N means no pool.
  const hasPool = home.swimmingPool ? home.swimmingPool !== 'N' : undefined;
  return compact({
    occupancy: OWNER_OCCUPIED_FORMS.includes(policyForm) ? 'Owner Occupied' : undefined,
    foundation: BRANCH_TO_EZLYNX_FOUNDATION[home.basementType],
    halfBaths: halfBaths(home.numHalfBathrooms),
    // EZLynx's dropdown runs 1-20 and is left empty for none.
    woodStoves: home.numWoodBurningStoves > 0 ? home.numWoodBurningStoves : undefined,
    purchasePrice: wholeDollars(home.purchaseAmount),
    ...mortgageeBoxes(home.mortgageDetails),
    yearBuilt: home.yearBuilt,
    squareFeet: home.sqFt,
    stories: home.numStories != null ? Math.round(home.numStories) : undefined,
    construction: translateToEzlynx(BRANCH_TO_EZLYNX_CONSTRUCTION, home.constructionType),
    exteriorWalls: translateToEzlynx(BRANCH_TO_EZLYNX_EXTERIOR_WALLS, home.exteriorWallType),
    roof: translateRoofToEzlynx(home.roofType, state),
    roofDesign: translateToEzlynx(BRANCH_TO_EZLYNX_ROOF_DESIGN, home.roofShape),
    roofYear: home.roofYear,
    roofUpdate: roofUpdate(home),
    dwellingType: translateToEzlynx(BRANCH_TO_EZLYNX_DWELLING, home.typeOfHome),
    dwellingUse: translateToEzlynx(BRANCH_TO_EZLYNX_DWELLING_USE, home.ownershipStatus),
    occupants: home.numOccupants,
    fullBaths: home.numFullBathrooms,
    protectionClass: home.fpc,
    swimmingPool: yesNo(hasPool),
    trampoline: yesNo(home.hasTrampoline),
    purchaseDate: formatDate(home.purchaseDate)
  });
}

function buildHomePolicy({ quote, optionType, policyForm }) {
  const global = quote.global || {};
  const shared = {
    ...sharedPolicyAnswers(optionType),
    priorCarrier: global.currentHomeownersCarrier ? normalizePriorCarrier(global.currentHomeownersCarrier) : undefined
  };
  if (policyForm === 'HO4') {
    const coverage = quote.rentersCoverage || {};
    return compact({
      ...shared,
      effectiveDate: formatDate(global.rentersEffectiveDate),
      policyForm,
      personalProperty: coverage.coverageCD,
      liability: coverage.coverageX,
      medicalPayments: coverage.coverageY,
      deductible: coverage.deductible
    });
  }
  const isCondo = policyForm === 'HO6';
  const coverage = (isCondo ? quote.condoCoverage : quote.homeCoverage) || {};
  return compact({
    ...shared,
    effectiveDate: formatDate(isCondo ? global.condoEffectiveDate : global.homeEffectiveDate),
    policyForm,
    replacementCost: wholeDollars(quote.home?.replacementCostEstimate?.total),
    dwelling: wholeDollars(coverage.coverageA),
    otherStructures: percentOfDwelling(coverage.coverageBPctOfA),
    personalProperty: percentOfDwelling(coverage.coverageCPctOfA),
    lossOfUse: percentOfDwelling(coverage.coverageDPctOfA),
    liability: coverage.coverageX,
    medicalPayments: coverage.coverageF ?? coverage.coverageY,
    deductible: formatHomeDeductible(coverage.deductibleAllOther)
  });
}

/**
 * `selectedOption` is the offer option the agent is looking at; `lines` comes from
 * getEasyKeyLines for the option the agent is looking at. Returns null when the offer has no quote.
 */
export function buildEasyKeyClipboard({ offer, selectedOption, lines }) {
  const quote = offer?.quote;
  if (!quote) {
    return null;
  }
  const optionType = quote.selectedOption || selectedOption?.type;
  const drivers = quote.drivers || [];
  const cars = quote.cars || [];
  const primaryDriver = drivers.find((driver) => driver.isPrimary) || drivers[0];
  const coApplicantDriver = findCoApplicantDriver(drivers);
  const policyForm = lines.hasProperty ? deriveHomePolicyType(optionType) : null;
  // The home being quoted, when it's where the insured lives.
  const residence = lines.hasProperty ? { home: quote.home, policyForm } : {};
  const isPrimaryResidence = PRIMARY_RESIDENCE_STATUSES.includes(quote.home?.ownershipStatus);
  const residenceFromHome =
    lines.hasProperty && isPrimaryResidence ? RESIDENCE_FROM_POLICY_FORM[policyForm] : undefined;

  return {
    version: EASYKEY_CLIPBOARD_VERSION,
    offerId: offer.id,
    insuredName: [quote.fname, quote.lname].filter(Boolean).join(' '),
    policyType: optionType || '',
    applicant: buildApplicant({ quote, primaryDriver, residence }),
    coApplicant: coApplicantDriver
      ? compact({ ...buildPerson(coApplicantDriver), ...buildLicense(coApplicantDriver), relation: 'Spouse' })
      : null,
    drivers: lines.hasAuto ? drivers.map(buildDriver) : [],
    vehicles: lines.hasAuto ? cars.map((car) => buildVehicle(car, drivers)) : [],
    autoPolicy: lines.hasAuto ? buildAutoPolicy({ quote, cars, optionType, residenceFromHome }) : null,
    property: lines.hasProperty ? buildProperty({ quote, policyForm }) : null,
    homePolicy: lines.hasProperty ? buildHomePolicy({ quote, optionType, policyForm }) : null
  };
}
